import { useEffect, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { DISCOVER } from "@/constants/testIds";
import { toast, Toaster } from "sonner";
import { Search, Check, Download, Sparkles, ExternalLink } from "lucide-react";
import { US_STATES, CA_CITIES } from "@/lib/formHelpers";

export default function LeadDiscovery() {
  const [industries, setIndustries] = useState([]);
  const [industry, setIndustry] = useState("Plumber");
  const [city, setCity] = useState("Los Angeles");
  const [state, setState] = useState("CA");
  const [results, setResults] = useState([]);
  const [source, setSource] = useState(null);
  const [sourceMode, setSourceMode] = useState("both");
  const [counts, setCounts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState({});
  const [building, setBuilding] = useState({});
  const [selected, setSelected] = useState({}); // {row.id: true}
  const [bulkBusy, setBulkBusy] = useState(false);
  // maps discovery row.id -> { leadId, websiteId }
  const [built, setBuilt] = useState({});

  useEffect(() => {
    api.get("/industries").then(({ data }) => {
      setIndustries(data.industries);
    });
  }, []);

  const search = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const { data } = await api.post("/leads/discover", {
        industry,
        city,
        state,
        count: 12,
        source: sourceMode,
      });
      setResults(data.results);
      setSource(data.source || "mock");
      setCounts(data.counts || null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const importOne = async (row) => {
    setImporting((s) => ({ ...s, [row.id]: true }));
    try {
      const { data: lead } = await api.post("/leads/import", row);
      setResults((rs) =>
        rs.map((r) => (r.id === row.id ? { ...r, already_imported: true } : r))
      );
      setBuilt((b) => ({ ...b, [row.id]: { ...(b[row.id] || {}), leadId: lead.id } }));
      toast.success(`Imported ${row.name}`);
      return lead;
    } catch (err) {
      toast.error(formatApiError(err));
      return null;
    } finally {
      setImporting((s) => ({ ...s, [row.id]: false }));
    }
  };

  const buildWebsite = async (row) => {
    setBuilding((s) => ({ ...s, [row.id]: true }));
    try {
      let leadId = built[row.id]?.leadId;
      if (!leadId) {
        const lead = await api.post("/leads/import", row).then((r) => r.data);
        leadId = lead.id;
        setResults((rs) =>
          rs.map((r) => (r.id === row.id ? { ...r, already_imported: true } : r))
        );
      }
      const { data: site } = await api.post("/websites/generate", { lead_id: leadId });
      setBuilt((b) => ({ ...b, [row.id]: { leadId, websiteId: site.id } }));
      toast.success(`Website built for ${row.name}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBuilding((s) => ({ ...s, [row.id]: false }));
    }
  };

  const importAll = async () => {
    const pending = results.filter((r) => !r.already_imported);
    for (const r of pending) {
      // sequential to avoid race
      // eslint-disable-next-line no-await-in-loop
      await importOne(r);
    }
    toast.success(`Imported ${pending.length} leads`);
  };

  const toggleSelect = (rowId) => {
    setSelected((s) => ({ ...s, [rowId]: !s[rowId] }));
  };

  const toggleSelectAll = () => {
    const eligible = results.filter((r) => (r.phone || "").trim());
    const allSelected = eligible.length > 0 && eligible.every((r) => selected[r.id]);
    if (allSelected) {
      setSelected({});
    } else {
      const next = {};
      eligible.forEach((r) => (next[r.id] = true));
      setSelected(next);
    }
  };

  const bulkCallSelected = async () => {
    const chosen = results.filter((r) => selected[r.id] && (r.phone || "").trim());
    if (chosen.length === 0) {
      toast.error("Select at least one lead with a phone number");
      return;
    }
    if (!window.confirm(`Fire AI voice calls to ${chosen.length} lead${chosen.length === 1 ? "" : "s"}? Each call costs ~$0.11/min.`)) return;
    setBulkBusy(true);
    try {
      // Ensure each is imported first so we have a lead_id to pass to Vapi
      const leadIds = [];
      for (const row of chosen) {
        let leadId = row.already_imported ? null : null;
        // Import if not yet
        // eslint-disable-next-line no-await-in-loop
        const { data: lead } = await api.post("/leads/import", row);
        leadId = lead.id;
        leadIds.push(leadId);
      }
      const { data } = await api.post("/vapi/call/bulk", { lead_ids: leadIds });
      const q = data.counts?.queued || 0;
      const s = data.counts?.skipped || 0;
      toast.success(`Queued ${q} AI calls${s ? ` · Skipped ${s}` : ""}. Track live at /outreach`);
      setSelected({});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="p-10">
      <Toaster position="top-right" richColors />
      <div className="mb-8">
        <div className="overline mb-3">Prospecting</div>
        <h1 className="font-display text-4xl font-black tracking-tighter mb-2">
          Find businesses without a website
        </h1>
        <p className="text-neutral-500 max-w-xl">
          Search local listings by industry and city. Every result below is a
          business currently operating without a public website.
        </p>
      </div>

      <form
        onSubmit={search}
        className="bg-white border border-neutral-200 p-6 grid grid-cols-1 md:grid-cols-5 gap-4 items-end mb-8"
      >
        <div>
          <label className="overline block mb-2">Industry</label>
          <select
            data-testid={DISCOVER.industrySelect}
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="w-full px-3 py-3 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black"
          >
            {industries.map((i) => (
              <option key={i}>{i}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="overline block mb-2">City</label>
          <input
            data-testid={DISCOVER.cityInput}
            value={city}
            onChange={(e) => setCity(e.target.value)}
            list={state === "CA" ? "ca-cities-list" : undefined}
            className="w-full px-3 py-3 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black"
            required
            placeholder={state === "CA" ? "Type or pick a CA city…" : "City"}
          />
          {state === "CA" && (
            <datalist id="ca-cities-list">
              {CA_CITIES.map((c) => (<option key={c} value={c} />))}
            </datalist>
          )}
        </div>
        <div>
          <label className="overline block mb-2">State</label>
          <select
            data-testid={DISCOVER.stateInput}
            value={state}
            onChange={(e) => setState(e.target.value)}
            className="w-full px-3 py-3 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black"
            required
          >
            {US_STATES.map(([code, name]) => (
              <option key={code} value={code}>{code} — {name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="overline block mb-2">Source</label>
          <select
            data-testid="discover-source-select"
            value={sourceMode}
            onChange={(e) => setSourceMode(e.target.value)}
            className="w-full px-3 py-3 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black"
          >
            <option value="both">Both (recommended)</option>
            <option value="google">Google Places only</option>
            <option value="osm">OpenStreetMap only</option>
            <option value="auto">Auto (Google first)</option>
          </select>
        </div>
        <button
          data-testid={DISCOVER.searchBtn}
          className="lf-btn-primary flex items-center justify-center gap-2"
          disabled={busy}
          type="submit"
        >
          <Search className="w-4 h-4" />
          {busy ? "Searching…" : "Search"}
        </button>
      </form>

      {source && (
        <div className="mb-4 flex items-center gap-2 text-xs flex-wrap">
          {source === "google_places" && <span className="pill pill-interested">Live · Google Places</span>}
          {source === "openstreetmap" && <span className="pill pill-interested">Live · OpenStreetMap</span>}
          {source === "google_places+osm" && <span className="pill pill-interested">Live · Google + OpenStreetMap (deduped)</span>}
          {source === "mock_fallback" && <span className="pill pill-website_sent">Live sources failed · showing demo data</span>}
          {source === "mock" && <span className="pill pill-new">Demo data (no API key)</span>}
          {counts && (
            <span className="text-neutral-500 font-mono">
              Google: {counts.google} · OSM: {counts.osm} · after dedupe: {counts.combined}
            </span>
          )}
        </div>
      )}

      {results.length > 0 && (
        <div className="lf-fade-up">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="overline mb-1">
                {results.length} businesses found
              </div>
              <h2 className="font-display text-xl font-semibold">
                {industry}s in {city}, {state} with no website
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {Object.values(selected).some(Boolean) && (
                <button
                  data-testid="bulk-call-selected-btn"
                  onClick={bulkCallSelected}
                  disabled={bulkBusy}
                  className="px-4 py-2 bg-[#E24E1B] text-white font-semibold text-sm flex items-center gap-2 hover:bg-[#0F2340] transition"
                >
                  📞 {bulkBusy ? "Queuing…" : `Call ${Object.values(selected).filter(Boolean).length} with AI`}
                </button>
              )}
              <button
                data-testid={DISCOVER.importAllBtn}
                className="lf-btn-outline flex items-center gap-2"
                onClick={importAll}
              >
                <Download className="w-4 h-4" />
                Import all
              </button>
            </div>
          </div>

          <div
            data-testid={DISCOVER.resultsTable}
            className="bg-white border border-neutral-200 overflow-x-auto"
          >
            <table className="lf-grid">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      onChange={toggleSelectAll}
                      checked={
                        results.filter((r) => (r.phone || "").trim()).length > 0 &&
                        results.filter((r) => (r.phone || "").trim()).every((r) => selected[r.id])
                      }
                      data-testid="discover-select-all"
                      title="Select all with phone"
                    />
                  </th>
                  <th>Business</th>
                  <th>Address</th>
                  <th>Phone</th>
                  <th>Rating</th>
                  <th>Website</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => {
                  const info = built[r.id] || {};
                  const isBuilding = !!building[r.id];
                  const hasPhone = !!(r.phone || "").trim();
                  return (
                  <tr key={r.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!selected[r.id]}
                        disabled={!hasPhone}
                        onChange={() => toggleSelect(r.id)}
                        data-testid={`discover-select-${r.id}`}
                        title={hasPhone ? "Select for bulk AI call" : "Needs phone to call"}
                      />
                    </td>
                    <td>
                      <div className="font-semibold">{r.name}</div>
                      <div className="text-xs text-neutral-500 flex items-center gap-1.5">
                        <span>{r.industry}</span>
                        {r.google_place_id && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 font-mono uppercase tracking-wider">Google</span>
                        )}
                        {r.osm_id && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono uppercase tracking-wider">OSM</span>
                        )}
                        {r.phone_enriched && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 font-mono uppercase tracking-wider" title="Phone found via Google enrichment">📞 enriched</span>
                        )}
                      </div>
                    </td>
                    <td className="text-neutral-600">{r.address}</td>
                    <td className="font-mono text-xs">{r.phone}</td>
                    <td>
                      <span className="font-semibold">{r.rating}</span>
                      <span className="text-neutral-400 text-xs">
                        {" "}
                        ({r.review_count})
                      </span>
                    </td>
                    <td>
                      {info.websiteId ? (
                        <a
                          data-testid={DISCOVER.viewSiteLink(r.id)}
                          href={`${API}/preview/${info.websiteId}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs font-semibold border-b border-black pb-0.5"
                        >
                          <ExternalLink className="w-3 h-3" /> View site
                        </a>
                      ) : (
                        <button
                          data-testid={DISCOVER.buildWebsiteBtn(r.id)}
                          onClick={() => buildWebsite(r)}
                          disabled={isBuilding}
                          className="lf-btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
                        >
                          <Sparkles className="w-3 h-3" />
                          {isBuilding ? "Building…" : "Build Website"}
                        </button>
                      )}
                    </td>
                    <td className="text-right">
                      {r.already_imported ? (
                        <span className="pill pill-interested inline-flex items-center gap-1">
                          <Check className="w-3 h-3" /> Imported
                        </span>
                      ) : (
                        <button
                          data-testid={DISCOVER.importBtn(r.id)}
                          onClick={() => importOne(r)}
                          disabled={importing[r.id]}
                          className="lf-btn-outline text-xs px-3 py-1.5"
                        >
                          {importing[r.id] ? "…" : "Import"}
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {results.length === 0 && !busy && source === "google_places" && (
        <div className="border border-dashed border-neutral-300 p-16 text-center">
          <div className="overline mb-3">No leads without a website</div>
          <p className="text-neutral-500 max-w-md mx-auto">
            Every {industry.toLowerCase()} Google found in {city}, {state} already
            has a website. Try a smaller town or a different industry — the best
            leads live in secondary markets.
          </p>
        </div>
      )}

      {results.length === 0 && !busy && source !== "google_places" && (
        <div className="border border-dashed border-neutral-300 p-16 text-center">
          <div className="overline mb-3">Awaiting search</div>
          <p className="text-neutral-500 max-w-md mx-auto">
            Pick an industry and city, then hit search to see local businesses
            missing a website.
          </p>
        </div>
      )}
    </div>
  );
}
